import type {ThreeEvent} from "@react-three/fiber";
import PointerInteractions from "./PointerInteractions.ts";
import type {SceneBoundaries, SourceState} from "../Sources";
import type {SidebarValues} from "../App";
import {type Dispatch, type RefObject, type SetStateAction} from "react";
import type * as THREE from "three";
import {PositionController} from "./PositionController.ts";
import {type TouchingBoundary, TouchingNone} from "../Scene.tsx";

export type ShapeControllerProps = {
    bounds: SceneBoundaries;
    panning: { horizontal: boolean, vertical: boolean };
    setIsHovered: Dispatch<SetStateAction<boolean>>;
    setPosition: Dispatch<SetStateAction<{ x: number; y: number; z: number }>>;
    setTouchingBoundary: Dispatch<SetStateAction<TouchingBoundary>>;
    setTouchingObject: Dispatch<SetStateAction<boolean>>;
    doClicked: () => void;
    setValues: SidebarValues;
};

export type OrientationData = { rotY?: number, rotX?: number };

export type ShapeProps = {
    position: { x: number; y: number; z: number };
    diameter: number; minZ: number;
    state: SourceState;
    controller: ShapeController;
    orientation?: OrientationData;
    touchingObject: boolean;
};

export type PointerHandlers = {
    onPointerOver: (e: ThreeEvent<PointerEvent>) => void;
    onPointerOut: (e: ThreeEvent<PointerEvent>) => void;
    onPointerDown: (e: ThreeEvent<PointerEvent>) => void;
    onPointerMove: (e: ThreeEvent<PointerEvent>) => void;
    onPointerUp: (e: ThreeEvent<PointerEvent>) => void;
    onWheel: (e: ThreeEvent<WheelEvent>) => void;
};


/**
 * Controller for all interactive shapes.
 *
 * It holds the mesh reference and the controller for user interaction.
 *
 * It's created and disposed with the shape.
 */
export class ShapeController {

    private readonly interactions: PointerInteractions;
    private readonly meshRef: RefObject<THREE.Mesh | null>;

    private readonly setPosition: Dispatch<SetStateAction<{ x: number; y: number; z: number }>>;
    private readonly setTouchingObject: Dispatch<SetStateAction<boolean>>;

    private positionController: PositionController;
    private touchingTimeout: number | null = null;

    private flashes: number = 0;

    private readonly setTouchingBoundary: Dispatch<SetStateAction<TouchingBoundary>>;

    constructor(props: ShapeControllerProps, meshRef: RefObject<THREE.Mesh | null>) {
        this.setPosition = props.setPosition;
        this.setTouchingBoundary = props.setTouchingBoundary;
        this.setTouchingObject = props.setTouchingObject;
        this.meshRef = meshRef;

        this.interactions = new PointerInteractions(
            props.setIsHovered,
            props.doClicked,
            this.doMove.bind(this),
            props.setValues
        );

        this.positionController = new PositionController(
            props.bounds,
            props.panning,
            props.setValues
        );
    }


    getMeshRef(): RefObject<THREE.Mesh | null> {
        return this.meshRef;
    }

    getPointerHandlers(): PointerHandlers {
        return this.interactions.getPointerHandlers();
    }

    private doMove(delta: { dx?: number; dy?: number; dz?: number }): void {

        const mesh = this.meshRef.current;
        if (!mesh) return;

        // if (!delta.dx && !delta.dy && !delta.dz) {
        //     console.log("no move");
        // } else {
        //     console.log("do move");
        // }

        // calculate the new position and update if changed
        const {changed, newPosition, atBoundary} = this.positionController.calculatePosition(mesh.position, delta);
        if (changed) {
            this.setPosition(newPosition);
        }

        if (changed && this.positionController.hitBoundary()) {
            //console.log("boundary flash");
            this.flash(200, atBoundary);
        }

        if ( !changed && this.positionController.highSpeed() ) {
            //console.log("high speed flash");
            this.flashUntil(300, atBoundary, () => !this.positionController.highSpeed());
        }

    }

    private flash(durationMS: number, atBoundary: TouchingBoundary): void {
        this.flashes += 1;
        this.setTouchingObject(true);
        this.setTouchingBoundary(atBoundary);

        // Reset to normal after duration (in milliseconds)
        this.touchingTimeout = window.setTimeout(() => {
            this.flashes -= 1;
            if (this.flashes === 0) {
                this.setTouchingObject(false);
                this.setTouchingBoundary(TouchingNone);
            }
            this.touchingTimeout = null;
        }, durationMS);
    }

    private flashUntil(iterationMS: number, atBoundary: TouchingBoundary, offCondition: () => boolean): void {
        this.flashes += 1;
        this.setTouchingObject(true);
        this.setTouchingBoundary(atBoundary);

        this.waitUntil(iterationMS, offCondition).then(() => {
            this.flashes -= 1;
            if (this.flashes <= 0) {
                this.setTouchingObject(false);
                this.setTouchingBoundary(TouchingNone);
            }
        });
    }

    async waitUntil(intervalMS: number, condition: () => boolean) {
        do {
            await new Promise(resolve => setTimeout(resolve, intervalMS));
        } while (!condition());
    }

    dispose(): void {
        // Clear timeout if exists
        if (this.touchingTimeout !== null) {
            clearTimeout(this.touchingTimeout);
            this.touchingTimeout = null;
        }
        this.interactions.dispose();
    }
}